import { useEffect, useRef } from 'react';
import { useLocation, useMatches } from 'react-router-dom';
import { setCommonAttribute } from '../core/telemetrySdk';

/** Common-attribute key under which the current route is published. */
export const ROUTE_ATTRIBUTE = 'route';

/**
 * The route `handle` contract this package reads, following the React Router
 * `handle` convention (https://reactrouter.com/start/framework/route-module#handle).
 * Deliberately minimal — consumers should extend it with their own fields
 * (breadcrumbs, titles, ...); everything beyond `routeName` is ignored here.
 */
export type TelemetryRouteHandle = {
  /** Stable, low-cardinality name used to dimension metrics by route. */
  routeName: string;
};

/** Narrows an unknown route `handle` to one that carries a `routeName`. */
export function hasRouteName(handle: unknown): handle is TelemetryRouteHandle {
  return (
    typeof handle === 'object' && handle !== null && typeof (handle as Record<string, unknown>).routeName === 'string'
  );
}

/**
 * Publishes the current route into the telemetry common-attributes store, so
 * metrics (e.g. Core Web Vitals) are dimensioned by route. Call from a component
 * inside the React Router tree (it uses `useLocation` / `useMatches`).
 *
 * The route name comes from the nearest match (leaf to root) whose `handle`
 * carries a `routeName` ({@link TelemetryRouteHandle}); otherwise it falls back
 * to the pathname with ids masked. Declare names on routes via the `handle`
 * prop:
 *
 * ```tsx
 * // Plain route — handle is the native React Router prop:
 * <Route path="login" handle={{ routeName: 'login' }} element={<Login />} />
 *
 * // Or via an app-level helper that pairs the handle with the element,
 * // e.g. the webserver's page():
 * <Route path="ai" {...page(AiAssistant, { handle: { routeName: 'ai-assistant' }, permissions: 'login' })} />
 * ```
 *
 * Publishing is intentionally decoupled from SDK initialization: the store is
 * just data, read lazily by instruments at record time. We must write the route
 * eagerly — even before `setupTelemetry` runs — because a metric (e.g. LCP) can
 * be recorded before the next route change would re-run this effect. Gating on
 * init would leave such early metrics unattributed.
 */
export function useRouteTelemetry(): void {
  const location = useLocation();
  const matches = useMatches();
  const prevRouteNameRef = useRef<string | null>(null);

  useEffect(() => {
    // Guard on the resolved route name, not the pathname: the name depends on
    // `matches` too, so a route whose `routeName` resolves after the initial
    // render (e.g. a lazily-loaded route module) must still re-publish even
    // though the pathname is unchanged. This also skips no-op writes when only
    // search params change, since those leave the route name the same.
    const routeName = resolveRouteName(location.pathname, matches);
    if (prevRouteNameRef.current === routeName) return;
    prevRouteNameRef.current = routeName;

    setCommonAttribute(ROUTE_ATTRIBUTE, routeName);
  }, [location, matches]);
}

function resolveRouteName(pathname: string, matches: ReturnType<typeof useMatches>): string {
  // Nearest match wins, walking leaf → root so a parent's routeName covers
  // element-less child routes (e.g. tab routes that only declare a path).
  for (let i = matches.length - 1; i >= 0; i--) {
    const { handle } = matches[i];
    if (hasRouteName(handle)) {
      return handle.routeName;
    }
  }

  // TODO(telemetry-logging): warn (once per pattern) that this route has no
  // routeName handle and is falling back to a path-derived name, so the gap is
  // visible and cardinality risk is flagged. Deferred until the package's
  // logging integration lands.
  return maskDynamicSegments(pathname);
}

// A whole path segment that is a GUID (Seeq item id) or all-numeric. The
// trailing `(?=\/|$)` anchors to a segment boundary so we only replace the
// segment when it ends — never a prefix of a longer one (which would yield
// names like `/:idextra`). GUIDs are matched case-insensitively.
const GUID_SEGMENT = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi;
const NUMERIC_SEGMENT = /\/\d+(?=\/|$)/g;

/**
 * Best-effort masking of dynamic path segments so a path-derived route name has
 * bounded cardinality. This is only a backstop: it cannot recognize slugs,
 * names, or other free-form ids, so the real fix for cardinality is to give the
 * route a `handle.routeName`.
 *
 * The pathname is lower-cased first: React Router matches case-insensitively by
 * default (no route opts into `caseSensitive`), so '/Home' and '/home' are the
 * same route and must fold to one name.
 */
function maskDynamicSegments(pathname: string): string {
  return (
    pathname
      .toLowerCase()
      .replace(GUID_SEGMENT, '/:id')
      .replace(NUMERIC_SEGMENT, '/:id')
      // Drop a trailing slash (but keep root '/') so '/login' and '/login/'
      // collapse to one name.
      .replace(/(.)\/$/, '$1') || '/'
  );
}
